/**
 * Cost type input: CPC/CPM/CPA toggle + amount + currency + Auto checkbox.
 * Mirrors the Binom panel's "Cost type" row.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Input } from '@/components/ui/form/input/Input';
import { Checkbox } from '@/components/ui/form/controls/Checkbox';
import { Button } from '@/components/ui/layout/Button';
import { cn } from '@/lib/utils';

const MODELS = ['CPC', 'CPM', 'CPA'] as const;
type Model = (typeof MODELS)[number];

export interface BinomCostInputProps {
  costModel: Model | undefined;
  amount: number | undefined;
  currency: string | undefined;
  isAuto: boolean | undefined;
  onChange: (
    patch: Partial<{
      costModel: Model;
      amount: number;
      currency: string;
      isAuto: boolean;
    }>,
  ) => void;
}

const BinomCostInput: FC<BinomCostInputProps> = ({
  costModel,
  amount,
  currency,
  isAuto,
  onChange,
}) => {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex items-center gap-1">
        {MODELS.map((m) => (
          <Button
            key={m}
            type="button"
            size="sm"
            variant={costModel === m ? undefined : 'outline'}
            className={cn('h-7 px-2 text-[11px]', costModel === m && 'shadow-sm')}
            onClick={() => onChange({ costModel: m })}
          >
            {m}
          </Button>
        ))}
      </div>
      <Input
        type="number"
        min={0}
        value={amount ?? 0}
        onChange={(e) => onChange({ amount: Number(e.target.value) || 0 })}
        className="h-7 w-20 text-right text-xs"
        aria-label="Cost amount"
      />
      <Input
        value={currency ?? ''}
        onChange={(e) => onChange({ currency: e.target.value.toUpperCase() })}
        className="h-7 w-16 text-xs"
        placeholder="USD"
        aria-label="Cost currency"
      />
      <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
        <Checkbox
          checked={Boolean(isAuto)}
          onCheckedChange={(v) => onChange({ isAuto: Boolean(v) })}
          aria-label="Auto"
        />
        <span>Auto</span>
      </label>
    </div>
  );
};

export default BinomCostInput;
